<template>
  <div>
    <div class="table-related-content">
      <ProposalFiltersBy
        class="mb-3"
        @paginate="$emit('paginate')"
      >
        <template #actions>
          <button
            class="button is-success"
            @click="emitDownload"
          >
            <ExportIcon :class="!isMobile ? 'mr-2' : ''" />
            {{ !isMobile ? 'Exportar CSV' : '' }}
          </button>
        </template>
      </ProposalFiltersBy>
    </div>
    <BaseTable
      :actions="[
        {
          label: 'Detalhes',
          icon: () => import('@/assets/icons/ProposalDetail.vue'),
          iconClass: 'not-stroke',
          action: (row) => goToDetailPage(row),
        },
        {
          label: 'Histórico',
          icon: () => import('@/assets/icons/ProposalHistory.vue'),
          iconClass: 'not-stroke',
          action: (row) => openModal('documentHistory', row),
        },
        {
          label: 'Antifraude',
          icon: () => import('@/assets/icons/ProposalAntifraud.vue'),
          iconClass: 'not-stroke',
          show: (row) => !['Em análise', 'Cancelada'].includes(row.status)
            && (row.status !== 'Paga' && isInternalUser),
          action: (row) => openModal('antifraud', row),
        },
        {
          label: 'Editar',
          icon: () => import('@/assets/icons/ProposalEdit.vue'),
          iconClass: 'not-stroke',
          action: (row) => goToUpdateProposalPage(row),
        },
      ]"
      :headers="[
        { name: 'document', label: 'CPF', show: isMobile },
        { name: 'name', label: 'Nome', show: isMobile },
        { name: 'client', label: 'Cliente', show: !isMobile },
        { name: 'status', label: 'Status', align: 'center' },
        { name: 'responsible', label: 'Responsável' },
        { name: 'created_at', label: 'Criado em' },
        { name: 'amount', label: 'Valor liberado' },
      ]"
      :data="proposals"
    >
      <template #client="{ row }">
        <CopyToClipboard class="is-highlighted">
          <span class="is-nowrap is-bold">
            {{ row.document }}
          </span>
        </CopyToClipboard>
        <p class="">
          {{ row?.full_name || row?.name }}
        </p>
      </template>
      <template #status="{ row }">
        <span
          v-if="row.proposal_id"
          v-tooltip="row.status === 'Cancelada' ? row.obs : null"
          class="tag"
          :class="proposalStatusCypher[row?.status]?.tag"
        >
          {{ proposalStatusCypher[row?.status]?.translation || row.status }}
        </span>
      </template>
      <template #responsible="{ row }">
        <span
          v-if="!isMobile"
          v-tooltip="getSubAndResponsible(row)"
          class="ellipsis left-aligned"
        >
          {{ row.responsible_name }}
        </span>
        <span v-else>{{ row.responsible_name }}</span>
      </template>
      <template #amount="{ row }">
        <p class="is-highlighted is-nowrap">
          {{ row.net_amount | money }}
          <span
            v-if="isSupplierBV(row.supplier_id) && !isMobile"
            v-tooltip="'Valor pode sofrer alterações após reanálise do banco.'"
          >
            <OutlineInfoIcon />
          </span>
        </p>
        <p
          v-if="!isMobile"
          class="is-nowrap"
        >
          {{ row.installments }}x
          {{ row.installment_value | money }}
        </p>
      </template>
      <template #mobile-footer="{ data }">
        <div class="is-flex is-justify-content-center mb-4">
          <span
            v-tooltip="data.status === 'Cancelada' ? data.obs : null"
            class="tag"
            :class="proposalStatusCypher[data?.status]?.tag"
          >
            {{ proposalStatusCypher[data?.status]?.translation || data.status }}
          </span>
        </div>
      </template>
    </BaseTable>

    <!-- Document History Modal -->
    <DocumentHistoryModal
      v-if="modal.documentHistory"
      :proposal="selectedProposal"
      @close="modal.documentHistory = false"
    />

    <!-- Anti fraud Modal -->
    <NewProposalAntifraudModal
      v-if="modal.antifraud"
      @close="modal.antifraud = false"
    />

    <!-- CCB Details Modal -->
    <CCBDetailsModal
      v-if="modal.ccbDetails"
      :proposal="selectedProposal"
      @close="modal.ccbDetails = false"
    />
  </div>
</template>

<script>
/** Core */
import { mapActions, mapState, mapGetters } from 'vuex';

/** Data */
import config from '@/data/config';
import { cypher as proposalStatusCypher } from '@/data/proposalStatusCypher.js';

import ProposalFiltersBy from '@/components/ProposalFiltersBy.vue';

export default {
  name: 'ProposalTable',
  components: {
    /** Icons */
    ExportIcon: () => import('@/assets/icons/ExportIcon.vue'),
    OutlineInfoIcon: () => import('@/assets/icons/OutlineInfo.vue'),

    /** Components */
    ProposalFiltersBy,
    BaseTable: () => import('@/components/Base/Table/index.vue'),
    NewProposalAntifraudModal: () => import('@/components/AntifraudModal/Index.vue'),
    DocumentHistoryModal: () => import('@/components/CGV/DocumentHistoryModal.vue'),
    CCBDetailsModal: () => import('@/components/CGV/CCBDetailsModal.vue'),
    CopyToClipboard: () => import('@/components/CopyToClipboard.vue'),
  },
  props: {
    proposals: {
      required: false,
      default: () => [],
      type: Array,
    },
  },
  data: () => ({
    selectedProposal: {},
    modal: {
      documentHistory: false,
      antifraud: false,
      ccbDetails: false,
    },
    selectedRows: [],
    proposalStatusCypher,
    noStatusToShowCCBModal: ['Em análise', 'Aguardando formalização', 'Pendência', 'Recusada', 'Cancelada'],
  }),
  computed: {
    ...mapState({
      suppliers: (state) => state.app.suppliers,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      isInternalUser: 'user/isInternalUser',
    }),
  },
  methods: {
    // utils
    ...mapActions({
      setProposalData: 'proposal/SET_PROPOSAL_DATA',
      setConsult: 'consult/SET_CONSULT',
    }),

    // handlers
    toggleChecked(id) {
      if (id) {
        const isChecked = !!this.selectedRows.find((elem) => elem === id);

        if (isChecked) {
          this.selectedRows.splice(this.selectedRows.indexOf(id), 1);
        } else {
          this.selectedRows.push(id);
        }

        return;
      }

      if (this.selectedRows?.length > 0) {
        this.selectedRows = [];
        return;
      }

      this.selectedRows = this.proposals.map((elem) => elem.proposal_id);
    },
    openModal(modalName, proposal) {
      /**
       * Se for o modal de detalhes do CCB e
       * o status da proposta for algum dos listados,
       * não irá abrir a modal.
       */
      if (
        modalName === 'ccbDetails' && this.noStatusToShowCCBModal.includes(proposal.status)
      ) {
        return;
      }

      if (proposal !== null) {
        this.setProposalData(proposal);
        if (proposal) this.selectedProposal = proposal;
      }
      this.modal[modalName] = true;
    },
    goToDetailPage(proposal) {
      this.setProposalData(proposal);
      this.$router.push(`/cgv/detalhes-proposta/${proposal.proposal_id}`);
    },
    async goToUpdateProposalPage(proposal) {
      await this.setConsult(proposal);
      this.$router.push(`/cgv/atualizar-proposta/${proposal.proposal_id}`);
    },
    emitDownload() {
      this.$emit('csv');
    },
    getSubAndResponsible({ promoter_name, responsible_name }) {
      return `Sub: ${promoter_name} - Resp: ${responsible_name}`;
    },
    isSupplierBV(supplier) {
      return supplier === config.SUPPLIER_ID_BV;
    },
  },
};

</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';
@import '@/assets/scss/src/_buttonsVariables.scss';

.table-related-content {
  font-style: italic;
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
}

.ccb__proposal_number.hasExternalId {
  text-decoration: underline;
  color: #4F46E5;
  cursor: pointer;
}

.ellipsis {
  span {
    display: block;
    max-width: 100px;
    overflow: hidden;

    text-overflow: ellipsis;
  }
}
</style>
